import { useState } from "react";
import { ALARM_STATE, isShelved, normalizeAlarmState } from "@lms/shared";

import type { AlarmRow } from "./AlarmBanner.js";
import { Icon } from "./Icon.js";
import { formatWhen, sortAlarms, stateLabel } from "../alarm-view.js";
import { ApiError } from "../api/client.js";
import { useI18n } from "../i18n.js";
import { useSession } from "../session.js";

/**
 * Il pannello delle notifiche, portato da `Home.qml`.
 *
 * Nell'app desktop gli allarmi non hanno una schermata: hanno **questo**, che
 * scende dalla campanella e resta sopra a quello che si stava guardando.
 * La differenza non e' di forma. Un allarme e' una cosa che interrompe: farlo
 * vivere in una pagina separata vuol dire che per vederlo bisogna aver deciso
 * di andarci, e chi sorveglia un impianto sta guardando i video.
 *
 * Dentro ci sono conferma e silenziamento, come nel `.qml`. I silenziati
 * **restano in elenco**: un allarme sparito dalla vista e' un allarme che
 * nessuno rivede, e la sua scadenza arriva senza che nessuno la aspetti.
 */
export function NotificationsPanel({
  alarms,
  clusterNames,
  onClose,
  onChanged,
}: {
  alarms: AlarmRow[];
  clusterNames: Record<string, string>;
  onClose: () => void;
  onChanged: () => void;
}) {
  const { t } = useI18n();
  const { api, can } = useSession();
  const [error, setError] = useState("");

  const unacked = alarms.filter((a) => a.state === "UNACK" || a.state === "RTNUN");
  const shelved = alarms.filter((a) => isShelved(a.state));
  const canAck = can("acknowledgeAlarms");

  const act = async (route: "alarms.acknowledge" | "alarms.shelve" | "alarms.acknowledge_all", body: unknown) => {
    try {
      await api.call(route, body);
      setError("");
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  };

  return (
    <>
      {/* Un clic fuori chiude: il pannello copre il video, e cercare la
          crocetta mentre si guarda un allarme e' tempo perso. */}
      <div className="notif__scrim" onClick={onClose} />
      <aside className="notif">
        <header className="notif__head">
          <Icon name="bell" size={14} />
          <b>{t("home.notifications")}</b>
          {unacked.length > 0 && (
            <span className="notif__pill notif__pill--crit">
              {unacked.length} {t("home.unread")}
            </span>
          )}
          {shelved.length > 0 && (
            <span className="notif__pill notif__pill--shlv">
              {shelved.length} {t("alarm.state.SHLVD")}
            </span>
          )}
          <span className="emissions__spacer" />
          {unacked.length > 1 && canAck && (
            <button className="notif__all" onClick={() => void act("alarms.acknowledge_all", {})}>
              {t("home.ackAll")}
            </button>
          )}
        </header>

        {error && <p className="alert">{error}</p>}

        <div className="notif__list">
          {sortAlarms(alarms).map((a) => (
            <NotifRow
              key={a.id}
              alarm={a}
              clusterName={clusterNames[a.clusterId] ?? "—"}
              canAck={canAck}
              onAck={() => void act("alarms.acknowledge", { id: a.id })}
              onShelve={() =>
                void act("alarms.shelve", { id: a.id, duration: 30, reason: "Operator shelved" })
              }
            />
          ))}
          {alarms.length === 0 && <p className="muted notif__empty">{t("home.noAlarms")}</p>}
        </div>
      </aside>
    </>
  );
}

function NotifRow({
  alarm,
  clusterName,
  canAck,
  onAck,
  onShelve,
}: {
  alarm: AlarmRow;
  clusterName: string;
  canAck: boolean;
  onAck: () => void;
  onShelve: () => void;
}) {
  const { t } = useI18n();
  const critical = alarm.level === "L2";
  const state = normalizeAlarmState(alarm.state);
  const tone =
    state === ALARM_STATE.rtnun
      ? "rtnun"
      : state === ALARM_STATE.shelved
        ? "shlv"
        : state === ALARM_STATE.acked
          ? "ack"
          : critical
            ? "crit"
            : "warn";
  const icon =
    state === ALARM_STATE.rtnun
      ? "rotate-ccw"
      : state === ALARM_STATE.shelved
        ? "archive"
        : critical
          ? "shield-alert"
          : "alert-triangle";

  return (
    <div className={`nrow nrow--${tone}`}>
      <span className="nrow__icon">
        <Icon name={icon} size={14} />
      </span>
      <div className="nrow__body">
        <div className="nrow__top">
          <span className={`nrow__lvl${critical ? " nrow__lvl--crit" : ""}`}>{alarm.level}</span>
          {alarm.priority && (
            <span className={`tag tag--${alarm.priority.toLowerCase()}`}>
              {t(`alarm.priority.${alarm.priority}`)}
            </span>
          )}
          <b>{clusterName}</b>
        </div>
        {/* Niente zero di comodo: un allarme senza valore registrato non ha
            letto zero. */}
        <div className="muted small">
          {alarm.ppmValue === null ? "—" : Number(alarm.ppmValue).toFixed(1)} ppm·m
        </div>
        <div className="nrow__meta">
          <span className={`tag tag--st-${tone}`}>{stateLabel(alarm.state, t)}</span>
          <span className="muted small">
            <Icon name="clock" size={10} /> {formatWhen(alarm.createdAt)}
          </span>
        </div>
      </div>
      {canAck && (
        <div className="nrow__acts">
          {(state === ALARM_STATE.unack || state === ALARM_STATE.rtnun) && (
            <button className="nrow__ack" onClick={onAck}>
              {t("home.ack")}
            </button>
          )}
          {state === ALARM_STATE.unack && (
            <button className="nrow__shelve" onClick={onShelve}>
              {t("alarm.shelve")}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
